import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface TwoFactorVerificationProps {
  userId: string;
  email: string;
  userName: string;
  password: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function TwoFactorVerification({
  userId,
  email,
  userName,
  password,
  onSuccess,
  onCancel,
}: TwoFactorVerificationProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { toast } = useToast();

  const sendCode = async () => {
    try {
      const { error } = await supabase.functions.invoke("send-2fa-code", {
        body: { userId, email, userName },
      });

      if (error) throw error;

      toast({
        title: "Código enviado",
        description: "Verifique seu e-mail",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao enviar código",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleResend = async () => {
    setResending(true);
    await sendCode();
    setResending(false);
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast({
        title: "Código inválido",
        description: "O código deve ter 6 dígitos",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "verify-2fa-code",
        {
          body: { userId, code },
        }
      );

      if (data?.success) {
        // Fazer login novamente após validação bem-sucedida
        const { error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (loginError) throw loginError;
        
        toast({
          title: "Verificação concluída",
          description: "Acesso autorizado",
        });
        onSuccess();
      } else {
        // Usar mensagem amigável do servidor ou mensagem padrão
        const errorMessage = data?.error || "Código inválido ou expirado";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      // Tratar mensagens técnicas e transformar em mensagens amigáveis
      let userMessage = error.message;
      
      if (error.message?.includes("Edge Function") || error.message?.includes("non-2xx")) {
        userMessage = "Código inválido ou expirado. Por favor, tente novamente.";
      } else if (error.message?.includes("Invalid login credentials")) {
        userMessage = "Erro ao autenticar. Por favor, faça login novamente.";
      }
      
      toast({
        title: "Erro na verificação",
        description: userMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Shield className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Verificação de Segurança</CardTitle>
          <CardDescription>
            Enviamos um código de 6 dígitos para {email}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">Código de Verificação</Label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-center">
              O código expira em 10 minutos
            </p>
          </div>

          <div className="space-y-2">
            <Button
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                "Verificar Código"
              )}
            </Button>

            <Button
              variant="outline"
              onClick={handleResend}
              disabled={resending}
              className="w-full"
            >
              {resending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Reenviar Código"
              )}
            </Button>

            <Button variant="ghost" onClick={onCancel} className="w-full">
              Cancelar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}